import { type ReactNode } from 'react';

import { Outlet } from 'react-router-dom';

import { CenteredContent } from '@/components/layouts/CenteredContent';
import { LayoutShell } from '@/components/layouts/LayoutShell';
import { MarketplaceBackgroundVideo } from '@/components/marketplace/MarketplaceBackgroundVideo';
import { useAuthStore } from '@/stores/store';

/**
 * The chrome variant a page wants from the unified shell. One module, one
 * `variant` prop — NOT four separate layout modules.
 *
 * - `default`   authed application chrome: auth-gated Sidebar + Header +
 *               ProfileCompletenessBanner + main + AppFooter + SessionCompanionBar
 *               (composed via LayoutShell, exactly as those render today). The
 *               sidebar renders iff the user is authenticated, so logged-out and
 *               public/legal/marketplace-browse visitors get no sidebar while
 *               authenticated visitors do (the "sidebar iff authenticated"
 *               principle) — no separate public variant is needed.
 * - `bare`      chromeless: content only. No Sidebar, Header, or footer chrome.
 * - `centered`  chromeless centered-over-globe treatment for the auth pages:
 *               the reusable CenteredContent wrapper plus the globe lift
 *               (`relative z-10`) + padding. It does NOT render Sidebar/Header/
 *               footer chrome and assumes a globe-hosting ancestor supplies the
 *               `<video>` it lifts above (the login/signup/forgot-password trio,
 *               nested under the `showGlobeBackground` host). Width is the nested
 *               AuthLayout's concern. Pages that want centering WITHIN normal
 *               chrome (e.g. the 404) should NOT use this variant — they use
 *               `default` and nest CenteredContent directly, so centering is
 *               orthogonal to chrome and independent of the globe.
 */
export type AppLayoutVariant = 'default' | 'bare' | 'centered';

export interface AppLayoutProps {
  /** Which chrome to render. Defaults to the authed application shell. */
  variant?: AppLayoutVariant;
  /**
   * Content to render inside the shell. When omitted, the shell renders
   * React Router's `<Outlet />` so existing `<Route element={<AppLayout />}>`
   * mounting keeps working unchanged.
   */
  children?: ReactNode;
  /**
   * Hide the sidebar even when authenticated (default variant only). The
   * sidebar is ALSO always hidden when the user is not authenticated.
   */
  hideSidebar?: boolean;
  /**
   * Host the single persistent spinning-globe background video
   * (MarketplaceBackgroundVideo, /globe.mp4) ONCE above the content. Defaults
   * OFF so the shell introduces no `<video>` node.
   *
   * When ON, the shell renders through the single LayoutShell path with the
   * video passed as LayoutShell's `background` slot — a flex sibling of the
   * scroller sized to the visible content REGION (GW-10550), so the globe stays
   * fixed to that region and never restretches as page content grows. The
   * sidebar remains auth-gated (present when authenticated, absent when not),
   * exactly like the `default` variant — no separate authenticated/unauthenticated
   * chrome branch.
   *
   * Mounted at a SHARED route ancestor, a single `<video>` DOM node persists
   * (no unmount/restart) as the user navigates among the pages nested under it
   * — the `/` landing, the four marketplace browse pages, and the auth trio.
   * Suspense MUST stay PER-CHILD in the route config (never wrapping this
   * shell's `<Outlet />`), or a first-visit lazy page load would suspend this
   * shell and remount/restart the video.
   */
  showGlobeBackground?: boolean;
}

/**
 * AppLayout — the single, variant-driven application shell. It owns the chrome
 * decision (which header/sidebar/footer to render) so routes no longer each
 * pick a layout. It reads `isAuthenticated` from `useAuthStore` internally to
 * auth-gate the sidebar.
 *
 * Consolidation target of GW-10432: renders `<Outlet />` when childless so
 * `<Route element={<AppLayout />}>` mounting works unchanged. The GlobeVideoLayout
 * module was retired into the `showGlobeBackground` globe host (GW-10438).
 * GW-10515 collapsed the redundant `public` variant into `default` (the shell
 * already auth-gates the sidebar) and renamed the auth-specific `auth-card`
 * variant to the generic `centered` variant (centering + globe lift only); the
 * `max-w-md` card width now lives in the nested AuthLayout. GW-10550 collapsed
 * the globe host's former authenticated/unauthenticated branches into this one
 * LayoutShell path: `showGlobeBackground` simply passes the single persistent
 * MarketplaceBackgroundVideo as LayoutShell's `background` slot (a
 * viewport-region backdrop), so there is no hand-rolled Header/main/footer
 * duplication and the globe stops restretching between pages of differing height.
 */
export function AppLayout({
  variant = 'default',
  children,
  hideSidebar,
  showGlobeBackground,
}: AppLayoutProps) {
  const { isAuthenticated } = useAuthStore();

  const content = children ?? <Outlet />;

  if (variant === 'bare') {
    return (
      <div className="bg-background flex min-h-screen flex-col">
        <div className="flex min-h-0 flex-1 flex-col p-6">{content}</div>
      </div>
    );
  }

  if (variant === 'centered') {
    // Chromeless centered-over-globe treatment for the auth pages. Centering is
    // delegated to the reusable CenteredContent primitive; this variant adds
    // ONLY the globe z-10 lift + padding. Width is the nested layout's concern
    // (see AuthLayout, which supplies the `max-w-md` card). Pages wanting
    // centering INSIDE chrome nest CenteredContent under `default` instead.
    return <CenteredContent className="relative z-10 p-4">{content}</CenteredContent>;
  }

  // variant === 'default' — authed application chrome via the single LayoutShell
  // path. The sidebar is gated on authentication (and suppressible via
  // hideSidebar); logged-out never renders a sidebar. LayoutShell owns Header +
  // ProfileCompletenessBanner + main + AppFooter + SessionCompanionBar exactly
  // as they render today.
  //
  // `showGlobeBackground` is a facet of the default variant, not a separate
  // decision surface: when set, the same shell additionally hosts the single
  // persistent globe video as LayoutShell's `background` slot (a
  // viewport-region backdrop) and opts out of the default p-6 (globe pages own
  // their own padding). It is only ever combined with `default` — `bare` and
  // `centered` return above — so the shell has one chrome path and the globe is
  // a background passed into it. This is the ONLY <video> mount point — a shared
  // route ancestor — so the single persistent node invariant holds.
  return (
    <LayoutShell
      hideSidebar={hideSidebar || !isAuthenticated}
      noPadding={showGlobeBackground || hideSidebar}
      background={showGlobeBackground ? <MarketplaceBackgroundVideo /> : undefined}
    >
      {content}
    </LayoutShell>
  );
}
